// SAFE-WRITE-BEGIN: frontend/src/components/admin/snapshotPaymentRow.ts
/** The Snapshot payments listing's shapes and status line (SnapshotPaymentsSection). Split out of
 *  the section to keep it under 400 lines (VAL-70-FOLLOWUPS). */
import { formatMediumDate } from '@/utils/date'
import type { CoveredRequest, FlagItem } from './_SnapshotRowNotes'

export interface SnapshotPaymentRow {
  engagement_id: string
  company: string
  owner_email: string
  estimate_date: string | null
  status: 'awaiting' | 'paid'
  product: string | null
  label: string | null
  /** AUDIT_VAL-70B MED-2: what this payment charged (before tax, after any promotion code). */
  price: number | null
  /** AUDIT_VAL-70B MED-2: the option's list price, shown beside `price` when they differ. */
  list_price?: number | null
  /** AUDIT_VAL-70B MED-3: per option, what is still due on this engagement (dollars). */
  amounts_due?: Record<string, number>
  paid_on: string | null
  credit_until: string | null
  email_to_masked?: string
  emailed_on?: string | null
  /** VAL-EMAIL-ENTRY-1: false = no client email on file, so the sender types one. */
  has_email?: boolean
  /** FIX_VAL-SNAPSHOT-ADMIN-EMAIL-2: false = the Snapshot cannot be printed yet; print_problem says why. */
  can_print?: boolean
  print_problem?: string | null
  /** AUDIT_VAL-70 M1/M2: what a person must act on (credit withdrawn, paid for an option held). */
  flags?: string[]
  /** Owner rulings 10-31 09:19: $0 options the owner asked for, waiting for a super_admin confirm. */
  covered_requests?: CoveredRequest[]
  /** VAL-70-FOLLOWUPS F3: the open flags with the id "Mark resolved" sends. */
  flag_items?: FlagItem[]
}

export interface Option {
  product: string
  label: string
  price: number
}

export interface Listing {
  rows: SnapshotPaymentRow[]
  options: Option[]
  suggested_reasons: string[]
  confirm_covered_label?: string
  /** VAL-70-FOLLOWUPS F3: WORDING FOR OWNER APPROVAL (server: RESOLVE_FLAG_LABEL). */
  resolve_flag_label?: string
}

/** Whole dollars as they are; dollars and cents with both cent digits (197.5 -> "197.50"). */
const dollars = (n: number | null | undefined) =>
  n == null ? '' : Number.isInteger(n) ? String(n) : n.toFixed(2)

export function statusText(r: SnapshotPaymentRow): string {
  if (r.status !== 'paid') return 'Awaiting payment'
  // VAL-FIX-AUDIT-68 M2: a tier has no Snapshot credit, so no "credit until".
  const credit = r.credit_until ? `, credit until ${formatMediumDate(r.credit_until)}` : ''
  // AUDIT_VAL-70B MED-2: the amount charged; the list price beside it when it differs.
  // WORDING FOR OWNER APPROVAL (admin screen only): "(list price $X)".
  const list = r.list_price != null && r.list_price !== r.price ? ` (list price $${dollars(r.list_price)})` : ''
  return `Paid ${formatMediumDate(r.paid_on)}, ${r.label} $${dollars(r.price)}${list}${credit}`
}
// SAFE-WRITE-EOF: frontend/src/components/admin/snapshotPaymentRow.ts
